import * as S from './styles';
import { colors } from '@/constants';
import { useEffect, useMemo, useState } from 'react';
import * as Packing from '@/components/packaging/styles';
import { ATTRIBUTES_LABELS, packingTypes } from '@/shared';
import { PackagesTypesProps, PackagingProductProps, ProductProps } from '@/types';

interface PackAttributeTableProps {
  product: ProductProps | undefined;
  pack: PackagesTypesProps;
}

const getPackagingUnits = (emb: PackagingProductProps) =>
  Number(emb?.QTD_EMBALAGEM || 0) * Number(emb?.QTD_MULTIPLO_VENDA || 0);

const getPackagingKey = (emb: PackagingProductProps) =>
  `${emb?.SG_EMBALAGEM || ''}:${getPackagingUnits(emb)}`;

const countFilledPackagingAttrs = (emb: PackagingProductProps) =>
  [
    emb.MD_ALTURA,
    emb.MD_LARGURA,
    emb.MD_PROFUNDIDADE,
    emb.MD_CUBAGEM,
    emb.QTD_CX_CAMADA,
    emb.QTD_CX_PALETE,
    emb.MD_PESO_LIQUIDO,
    emb.MD_PESO_BRUTO,
  ].filter((value) => value != null && value !== '').length;

const PackagingAttributesTable = ({ product, pack }: PackAttributeTableProps) => {
  const [selectedPack, setSelectedPack] = useState<PackagesTypesProps | null>(pack);

  const orderedPackaging = useMemo(() => {
    const sorted = [...(product?.EMBALAGEM || [])].sort(
      (a, b) => getPackagingUnits(b) - getPackagingUnits(a)
    );

    const uniqueByKey = new Map<string, PackagingProductProps>();
    for (const emb of sorted) {
      if (!emb?.SG_EMBALAGEM || getPackagingUnits(emb) <= 0) continue;

      const key = getPackagingKey(emb);
      const current = uniqueByKey.get(key);
      if (!current || countFilledPackagingAttrs(emb) > countFilledPackagingAttrs(current)) {
        uniqueByKey.set(key, emb);
      }
    }

    return [...uniqueByKey.values()];
  }, [product]);

  const selectedPackagingAttributes = useMemo(() => {
    const selectedPackaging = orderedPackaging.find((emb) => emb?.SG_EMBALAGEM === selectedPack);

    return [
      { MD_ALTURA: selectedPackaging?.MD_ALTURA },
      { MD_LARGURA: selectedPackaging?.MD_LARGURA },
      { MD_PROFUNDIDADE: selectedPackaging?.MD_PROFUNDIDADE },
      { MD_CUBAGEM: selectedPackaging?.MD_CUBAGEM },
      { QTD_CX_CAMADA: selectedPackaging?.QTD_CX_CAMADA },
      { QTD_CX_PALETE: selectedPackaging?.QTD_CX_PALETE },
      { MD_PESO_LIQUIDO: selectedPackaging?.MD_PESO_LIQUIDO },
      { MD_PESO_BRUTO: selectedPackaging?.MD_PESO_BRUTO },
    ];
  }, [orderedPackaging, selectedPack]);

  useEffect(() => {
    setTimeout(() => {
      setSelectedPack(pack || null);
    }, 300);
  }, [pack]);

  return (
    <>
      <S.PackagingRow>
        {orderedPackaging?.map((embalagem) => {
          const isActive = selectedPack === embalagem?.SG_EMBALAGEM;
          const packImage = packingTypes[embalagem?.SG_EMBALAGEM as PackagesTypesProps]?.icon;
          return (
            <Packing.BoxEmbalagem
              size={50}
              $fullBorder={isActive}
              $isactive={isActive}
              $unityPack={orderedPackaging.length === 1}
              onClick={() => {
                setSelectedPack(embalagem?.SG_EMBALAGEM);
              }}
              key={`pack-${product?.COD_PRODUTO}-${getPackagingKey(embalagem)}`}
            >
              {packImage && <Packing.PackImage src={packImage} alt={embalagem?.SG_EMBALAGEM ?? ''} />}
              {isActive && <Packing.CheckFill size={24} color={colors.alert.green110} />}

              <Packing.BoxLabel>
                <Packing.BoxQtdEmbalagem>
                  <Packing.QtdEmbalagem>{getPackagingUnits(embalagem)}/un</Packing.QtdEmbalagem>
                </Packing.BoxQtdEmbalagem>
              </Packing.BoxLabel>
            </Packing.BoxEmbalagem>
          );
        })}
      </S.PackagingRow>

      <S.TableBody>
        {selectedPackagingAttributes?.map((item, index) => {
          const [[key, value]] = Object.entries(item);
          const isOdd = (index + 1) % 2 !== 0;

          return (
            <S.TableRow key={`${key}-${index}`} $applyBackground={isOdd}>
              <S.TableCell $left $bgColor={!isOdd ? colors.gray.gray10 : colors.background1}>
                {ATTRIBUTES_LABELS[key as keyof typeof ATTRIBUTES_LABELS]}
              </S.TableCell>
              <S.TableCell $bgColor={isOdd ? colors.gray.gray10 : colors.gray.gray0}>{value}</S.TableCell>
            </S.TableRow>
          );
        })}
      </S.TableBody>
    </>
  );
};

export default PackagingAttributesTable;
